import { MutableRefObject, useEffect, useRef } from "react";
import getConfig from "next/config";

const { publicRuntimeConfig } = getConfig();

export const pushEvent = event => {
  if (publicRuntimeConfig.TAG_MANAGER) {
    if (typeof window === "undefined") return;

    (window as Window)?.dataLayer?.push(event);
    // для аналитиков
    console.log("[GTM] - push event:", event);
  }
};
let autocompleteKeyEnterEventFired = false;

/**
 * Creates badges string like: "badge1;badge2", except "thermolabil"
 */
const createBadgesString = (badges: Entity.ProductBadge[] = []) => {
  const filteredBadges = badges.filter(({ type }) => type !== "thermolabil");
  const badgesStr = filteredBadges.reduce((badgesStr, { title }, idx) => {
    const badgeEnd = idx !== filteredBadges.length - 1 ? ";" : "";

    return badgesStr + title + badgeEnd;
  }, "");

  return badgesStr?.length ? badgesStr : undefined;
};

export const useOnWatch = ({
  subject,
  onWatch,
  index,
  elementRef,
  delay,
}: {
  subject?: Entity.Product | Record<string, unknown>;
  onWatch?: Entity.Analytics.OnWatch;
  index?: string | number;
  elementRef?: MutableRefObject<HTMLElement>;
  delay?: number;
}) => {
  const observer = useRef(null);
  const watched = useRef(false);

  useEffect(() => {
    const unsubscribe = () => {
      if (observer.current) {
        observer.current.disconnect();
        elementRef.current && observer.current.unobserve(elementRef.current);
      }
    };
    if (onWatch && subject && !watched.current) {
      const callback = entries => {
        const entry = entries[0];

        if (entry.isIntersecting) {
          onWatch({
            subject,
            isIntersecting: entry.isIntersecting,
            entry,
            index,
          });
          watched.current = true;
          unsubscribe();
        }
      };
      const options = {
        threshold: 0.6,
        // если нужно указать паузу при просмотре
        delay,
      };
      if (elementRef.current) {
        observer.current = new IntersectionObserver(callback, options);
        observer.current.observe(elementRef.current);
      }
    }
    return unsubscribe;
  }, [subject, onWatch]);
};

export const gtm = {
  event: ({ action, category, label, value }) => {
    pushEvent({
      event: "GAEvent",
      eventAction: action,
      eventCategory: category,
      eventLabel: label,
      eventValue: value,
    });
  },
  pageView: (url: string) => {
    pushEvent({
      event: "pageview",
      page: url,
    });
  },
  productPageOpen: (product: Entity.Product, page = "Product Details") => {
    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        add: {
          products: [
            {
              name: product.name,
              id: product.id,
              price: product.price,
              brand: product.brand.name,
              category: product.category.path,
              isEmpty: !product.price,
              dimension1: createBadgesString(product.badges),
              dimension2: product.isBundle,
            },
          ],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": page,
      "gtm-ee-event-non-interaction": false,
    });
  },
  cartAdd: ({
    product,
    position,
    quantity,
    list = "Товары дня",
    category,
    price,
  }: {
    product: Entity.Product;
    position: number;
    quantity: number;
    list: string;
    category: Entity.Category;
    price: Partial<Entity.Product.Price>;
  }) => {
    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        add: {
          products: [
            {
              name: product.name,
              id: product.id,
              price: price.price,
              brand: product.brand?.name,
              category: category?.name,
              list,
              position,
              quantity,
              dimension1: createBadgesString(product.badges),
              dimension2: product.isBundle,
              dimension3: !!price.isExpirating,
            },
          ],
          actionField: { list, action: "add" },
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Adding a Product to a Shopping Cart",
      "gtm-ee-event-non-interaction": false,
    });
  },
  customReferrer: (prevUrl: string) => {
    pushEvent({ event: "custom_referrer", previous_page_url: prevUrl });
  },
  watchAdvert: (
    id: Entity.ID,
    name: string,
    position: string,
    creative: string
  ) => {
    pushEvent({
      ecommerce: {
        promoView: {
          promotions: [
            {
              id,
              name: name || "баннер без имени",
              position,
              creative,
            },
          ],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Promotion Impressions",
      "gtm-ee-event-non-interaction": true,
    });
  },
  clickAdvert: (
    id: Entity.ID,
    name: string,
    position: string,
    creative: string
  ) => {
    pushEvent({
      ecommerce: {
        promoClick: {
          promotions: [
            {
              id,
              name: name || "баннер без имени",
              position,
              creative,
            },
          ],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Promotion Clicks",
      "gtm-ee-event-non-interaction": false,
    });
  },
  clickProduct: (
    product: Entity.Product,
    position: number,
    list = "Товары дня",
    category?: Entity.Category
  ) => {
    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        impressions: [
          {
            name: product.name,
            id: product.id,
            price: product.price,
            brand: product.brand?.name,
            category: category?.name,
            dimension1: createBadgesString(product.badges),
            dimension2: product.isBundle,
            list,
            position,
          },
        ],
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Product Clicks",
      "gtm-ee-event-non-interaction": false,
    });
  },
  watchProduct: (
    product: Entity.Product,
    position: number,
    list = "Товары дня",
    category?: Entity.Category,
    inStock?: boolean
  ) => {
    const hasExpiringPrice = Boolean(
      product.prices?.find(price => price.isExpirating && price.price > 0)
    );
    const isEmpty = !inStock && !product.price;

    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        impressions: [
          {
            name: product.name,
            id: product.id,
            price: product.price,
            brand: product.brand?.name,
            category: category?.name,
            dimension1: createBadgesString(product.badges),
            dimension2: product.isBundle,
            dimension3: hasExpiringPrice,
            list,
            position,
            hasExpiringPrice,
            inStock,
            isEmpty,
          },
        ],
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Product Impressions",
      "gtm-ee-event-non-interaction": true,
    });
  },
  clickPromoBtn: () => {
    pushEvent({
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Поведение пользователя",
      "gtm-ee-event-action": "Клик по кнопке акции в хедере",
      "gtm-ee-event-non-interaction": false,
    });
  },
  clickDiscountsBtn: () => {
    pushEvent({
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Поведение пользователя",
      "gtm-ee-event-action": "Клик по кнопке скидки в хедере",
      "gtm-ee-event-non-interaction": false,
    });
  },
  clickISGBtn: () => {
    pushEvent({
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Поведение пользователя",
      "gtm-ee-event-action": "Клик по кнопке уценка в хедере",
      "gtm-ee-event-non-interaction": false,
    });
  },
  authModal: () => {
    pushEvent({ event: "Появление окна авторизации" });
  },
  userEnteredPhone: () => {
    pushEvent({ event: "Форма авторизации заполнение номера телефона" });
  },
  userClickedGetSMSPas: () => {
    pushEvent({ event: "Форма авторизации запрос пароля" });
  },
  userEnteredSMS: () => {
    pushEvent({ event: "Форма авторизации заполнение кода смс" });
  },
  registerModal: () => {
    pushEvent({ event: "Переход на форму регистрации" });
  },
  userGetNewCode: () => {
    pushEvent({ event: "Форма регистрации получить новый код" });
  },
  userClickedContinueBtn: () => {
    pushEvent({ event: "Форма регистрации клик по кнопке продолжить" });
  },
  userSetName: () => {
    pushEvent({ event: "Форма регистрации заполнение имени" });
  },
  successAuth: () => {
    pushEvent({ event: "Успешная авторизация" });
  },
  successRegister: () => {
    pushEvent({ event: "Успешная регистрация" });
  },
  userEnteredEmailForSubscription: (orderInfo = false, spam = false) => {
    pushEvent({
      event: "Заполнение e-mail",
      "order-info": orderInfo,
      spam,
    });
  },
  onAsideBannerWatch: () => {
    pushEvent({ event: "Показ рекламного баннера" });
  },
  onAsideBannerClick: () => {
    pushEvent({ event: "Клик по рекламному баннеру" });
  },
  authEmailFormShow: () => {
    pushEvent({ event: "Показ формы согласия 2" });
  },
  onLunaProductClick: (
    product: Entity.Product,
    analyticsMeta?: Entity.ProductAnalyticsMeta
  ) => {
    const hasExpiringPrice = Boolean(
      product.prices?.find(price => price.isExpirating && price.price > 0)
    );
    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        click: {
          actionField: { list: product.counters?.advertisement.name },
          products: [
            {
              id: product.id,
              name: product.name,
              price: product.price,
              brand: product.brand?.name,
              category: product.category.path,
              position: analyticsMeta.position,
              dimension1: createBadgesString(product.badges),
              dimension2: product.isBundle,
              dimension3: hasExpiringPrice,
              list: analyticsMeta.list,
              variant: null,
            },
          ],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Product Clicks",
      "gtm-ee-event-non-interaction": false,
    });
  },
  onLunaProductWatch: (
    product: Entity.Product,
    analyticsMeta?: Entity.ProductAnalyticsMeta
  ) => {
    const hasExpiringPrice = Boolean(
      product.prices?.find(price => price.isExpirating && price.price > 0)
    );
    pushEvent({
      ecommerce: {
        currencyCode: "RUB",
        impressions: [
          {
            id: product.id,
            name: product.name,
            price: product.price,
            brand: product.brand?.name,
            category: product.category.path,
            position: analyticsMeta.position,
            dimension1: createBadgesString(product.badges),
            dimension2: product.isBundle,
            dimension3: hasExpiringPrice,
            list: analyticsMeta.list,
            variant: null,
          },
        ],
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Product Impressions",
      "gtm-ee-event-non-interaction": true,
    });
  },
  onLunaBannerClick: (counters: Common.AdvertisementCounters) => {
    const { id, name, creative, position } = counters;

    pushEvent({
      ecommerce: {
        promoClick: {
          promotions: [{ id, name, creative, position }],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Promotion Clicks",
      "gtm-ee-event-non-interaction": false,
    });
  },
  onLunaBannerWatch: (counters: Common.AdvertisementCounters) => {
    const { id, name, creative, position } = counters;

    pushEvent({
      ecommerce: {
        promoView: {
          promotions: [{ id, name, creative, position }],
        },
      },
      event: "gtm-ee-event",
      "gtm-ee-event-category": "Enhanced Ecommerce",
      "gtm-ee-event-action": "Promotion Impressions",
      "gtm-ee-event-non-interaction": true,
    });
  },
  locationModalRequest: () => {
    pushEvent({ event: "Клик на регион" });
  },
  locationModalOpen: (region: string) => {
    pushEvent({ event: "Показ поп-апа", region });
  },
  locationModalChange: (region: string) => {
    pushEvent({ event: "Смена региона через поп-ап", region });
  },
  locationModalClose: (region: string) => {
    pushEvent({ event: "Закрытие поп-апа", region });
  },
  locationModalConfirm: (region: string) => {
    pushEvent({ event: "Подтверждение региона в поп-апе", region });
  },
  autocompleteFieldClick: () => {
    autocompleteKeyEnterEventFired = false;
    pushEvent({ event: "Клик на поиск" });
  },
  autocompleteShowAllClick: () => {
    pushEvent({ event: 'Клик на кнопку "Все товары" в поиске' });
  },
  autocompleteKeyPressed: () => {
    if (!autocompleteKeyEnterEventFired) {
      pushEvent({ event: "Ввод поискового запроса" });
      autocompleteKeyEnterEventFired = true;
    }
  },
  autocompleteSuggestionShow: (
    search_input: string,
    search_place:
      | "Горизонтальный поиск"
      | "Карта на шаге 2"
      | "Список на шаге 2"
  ) => {
    pushEvent({
      event: "Ввод поискового запроса с подсказками",
      search_input,
      search_place,
    });
  },
  autocompleteHistoryClick: (
    history_amount: number,
    history_row_num: number
  ) => {
    pushEvent({
      event: "Клик на товар из истории поиска",
      history_amount,
      history_row_num,
    });
  },
  autocompleteHistoryRemove: (
    history_amount: number,
    history_row_num: number
  ) => {
    pushEvent({
      event: "Удаление товара из истории",
      history_amount,
      history_row_num,
    });
  },
  autocompleteOftenClick: (often_search_row_num: number) => {
    pushEvent({
      event: "Клик на товар из часто ищут",
      often_search_row_num,
    });
  },
  autocompleteTapClick: (tap_amount: number, tap_row_num: number) => {
    pushEvent({
      event: "Клик на тапы",
      tap_amount,
      tap_row_num,
    });
  },
  autocompleteBrandShow: () => {
    pushEvent({ event: "Показ саджеста бренда в поиске" });
  },
  autocompleteBrandClick: () => {
    pushEvent({ event: "Клик по саджест бренда в поиске" });
  },
  autocompleteProductAdd: () => {
    pushEvent({ event: "Добавление в корзину товаров из поиска (саджесты)" });
  },
  onChoiceFromSearch: (query: string, choice: string = null) => {
    pushEvent({
      event: "Переход по поисковому запросу",
      query,
      choice,
    });
  },
  onTradeFormsShow(productId: Entity.Product["id"]) {
    pushEvent({
      event: "Показ блока Торговые предложения",
      product_id: productId,
    });
  },
  stockBannerMounted: () => {
    pushEvent({ event: "Показ баннера Товар участвует в акции" });
  },
  clickStockBanner: () => {
    pushEvent({
      event: "Товар участвует в акции",
      source: "action/more",
    });
  },
  stockModalOpen: () => {
    pushEvent({
      event: "Показ попапа Товар в корзине",
    });
  },
  stockModalClose: () => {
    pushEvent({
      event: "Закрыть попап Товар в корзине",
    });
  },
  clickStockModalReceivingShare: () => {
    pushEvent({
      event: "Клик на кнопку Получить скидку",
    });
  },
  clickStockModalGoToCart: () => {
    pushEvent({
      event: "Клик на кнопку Перейти в корзину в попапе Товар в корзине",
    });
  },
  clickStockModalProductChange: (quantity: number) => {
    pushEvent({
      event: "Изменение количества товара в попапе Товар в корзине",
      quantity,
    });
  },
  onTradeFormClick(group: string, value: string) {
    pushEvent({ event: `Клик по табу ${group}`, value });
  },
  onCheckExpirationProductInList(product: Entity.Product) {
    pushEvent({
      product_id: product?.id,
      event: 'Клик на radio_btn "годен до" в карточке товара в каталоге',
    });
  },
  preOrder: {
    onOpenCart: () => {
      pushEvent({
        event: "Загрузка корзины React",
      });
    },
    onOpenStep: (
      actionField: { step: number; pageType?: string },
      cartItems: Array<Entity.Cart.Item>
    ) => {
      pushEvent({
        ecommerce: {
          currencyCode: "RUB",
          checkout: {
            actionField,
            products: cartItems.map(item => ({
              name: item.product.name,
              id: item.product.id,
              price: item.price,
              category: "",
              quantity: item.quantity,
              brand: item.product.brand?.name || "",
              dimension1: createBadgesString(item.product.badges),
              dimension2: item.product.isBundle,
              dimension3: item.isExpirating,
              position:
                cartItems.findIndex(
                  i =>
                    i.product.id === item.product.id &&
                    i.product.priceTypeID === item.product.priceTypeID
                ) + 1,
            })),
          },
        },
        event: "gtm-ee-event",
        "gtm-ee-event-category": "Enhanced Ecommerce",
        "gtm-ee-event-action": `Checkout Step ${actionField.step}`,
        "gtm-ee-event-non-interaction": false,
      });
    },
    onReceiveTypeChange: (type: Entity.Cart.ReceiveType) => {
      pushEvent({
        event: "Выбор типа получения заказа",
        receive_type: type,
      });
    },
    onSelectAll: (bool: boolean) => {
      pushEvent({
        event: "Опция Выбрать все",
        choose_all: bool,
      });
    },
    onSelectProduct: (bool: boolean) => {
      pushEvent({
        event: "Выбор продукта",
        choose_all: bool,
      });
    },
    onDeleteSelected: () => {
      pushEvent({
        event: "Удалить выбранные",
      });
    },
    onDeleteAll: () => {
      pushEvent({
        event: "Очистить корзину",
      });
    },
    onFavoritesFilterClick: () => {
      pushEvent({ event: "Клик на фильтр избранные аптеки" });
    },
    onZoomIn: () => {
      pushEvent({ event: "Клик на zoom-in" });
    },
    onZoomOut: () => {
      pushEvent({ event: "Клик на zoom-out" });
    },
    onGeoClick: () => {
      pushEvent({ event: "Клик на определение гео" });
    },
    onSearchPharmacyClick: () => {
      pushEvent({ event: "Клик на поиск в корзине" });
    },
    onPharmaciesListClick: () => {
      pushEvent({ event: "Клик на список аптек" });
    },
    onClusterClick: () => {
      pushEvent({ event: "Клик на пин кластера аптек" });
    },
    onPlacemarkClick: (
      type?: Entity.Cart.ReceiveType,
      availableNumber?: string,
      pharmacyType?: string,
      doubleBonuses?: boolean
    ) => {
      pushEvent({
        event: "Клик на пин аптеки",
        delivery: type,
        available_number: availableNumber,
        pharm_type: pharmacyType,
        double_points: doubleBonuses,
      });
    },
    onOpenProductsSlider: () => {
      pushEvent({ event: "Раскрытие блока товаров на карточке аптеки" });
    },
    onSearchPharmacies: (search_input?: string) => {
      pushEvent({ event: "Ввод запроса в поиск по аптекам", search_input });
    },
    onPharmaciesMapSuggestClick: ({
      order,
      value,
    }: {
      order: number;
      value: string;
    }) => {
      pushEvent({
        event: "Клик на поисковый саджест адреса",
        suggest_order: order,
        suggest_value: value,
      });
    },
    onPharmaciesSuggestClick: () => {
      pushEvent({ event: "Клик на саджест адреса из поиска по аптекам" });
    },
    onAddressEdit: () => {
      pushEvent({ event: "Ввод адреса доставки" });
    },
    onDeliveryDateClick: (label: string, index) => {
      const text =
        label === "Today" ? "Today" : label === "Tomorrow" ? "Tomorrow" : index;

      pushEvent({
        event: "Выбор даты доставки",
        "delivery-date": text,
      });
    },
    onDeliveryTimeClick: (label: string) => {
      pushEvent({
        event: "Выбор интервала доставки",
        "delivery-time": label,
      });
    },
    onDeliveryTimeExpressClick: (value: string) => {
      pushEvent({
        event: "Выбор экспресс-доставки",
        delivery_service: value,
      });
    },
    onAddressSelect: (address: string) => {
      pushEvent({
        event: "Выбор адреса доставки",
        "delivery-address": address,
      });
    },
    onPaymentTypeClick: (paymentType: Entity.Cart.PaymentType) => {
      let type = "";

      switch (paymentType) {
        case "online":
          type = "online";
          break;
        case "card":
          type = "Card_to_courier";
          break;
        case "cash":
          type = "cash";
          break;
        case "pharmacy":
          type = "cash";
          break;
        default:
          break;
      }

      pushEvent({
        event: "Выбор типа оплаты",
        payment_type: type,
      });
    },
    onFilterOnlinePaymentClick: () => {
      pushEvent({ event: "Клик на фильтр онлайн оплаты" });
    },
    onPickupPaymentTypeClick: (paymentType: Entity.Cart.PaymentType) => {
      pushEvent({
        event: "Выбор типа оплаты",
        payment_type: paymentType === "online" ? "Онлайн оплата" : "В аптеке",
      });
    },
    onDeliveryNameEdit: () => {
      pushEvent({ event: "Заполнение имени" });
    },
    onDeliveryPhoneEdit: () => {
      pushEvent({ event: "Заполнение телефона" });
    },
    onDeliveryCheckClick: () => {
      pushEvent({ event: "Согласие на электронный чек" });
    },
    onEmailEdit: () => {
      pushEvent({ event: "Заполнение e-mail" });
    },
    onAddressUnavailable: (address: string) => {
      pushEvent({
        event: "Доставка недоступна по адресу",
        "delivery-address": address,
      });
    },
    onPromoCodeClick: () => {
      pushEvent({ event: "Клик по полю ввод промокод" });
    },
    onPromoCodeAccept: () => {
      pushEvent({ event: "Ввод промокода" });
    },
    onPromoCodeApply: ({
      promo_code,
      error,
    }: {
      promo_code: string;
      error: string;
    }) => {
      pushEvent({
        event: "promo_code_apply",
        promo_code,
        error,
      });
    },
    onBonusesClick: () => {
      pushEvent({ event: "Списать баллы" });
    },
    onCheckout: () => {
      pushEvent({ event: "Клик по кнопке оформить заказ" });
    },
    onSuccess: (
      actionField: {
        id: Entity.ID;
        affiliation: Entity.ID;
        revenue: number | string;
        shipping?: number;
      },
      cartItems: Array<Entity.Cart.Item>
    ) => {
      pushEvent({
        ecommerce: {
          currencyCode: "RUB",
          purchase: {
            actionField,
            products: cartItems.map(item => ({
              name: item.product.name,
              id: item.product.id,
              price: item.price,
              category: "",
              quantity: item.quantity,
              brand: item.product.brand?.name || "",
              dimension1: createBadgesString(item.product.badges),
              dimension2: item.product.isBundle,
              dimension3: item.isExpirating,
              position:
                cartItems.findIndex(
                  i =>
                    i.product.id === item.product.id &&
                    i.product.priceTypeID === item.product.priceTypeID
                ) + 1,
            })),
          },
        },
        event: "gtm-ee-event",
        "gtm-ee-event-category": "Enhanced Ecommerce",
        "gtm-ee-event-action": "Purchase",
        "gtm-ee-event-non-interaction": false,
      });
    },
    onAdd: (cartItems: Array<Entity.Cart.Item>, item: Entity.Cart.Item) => {
      pushEvent({
        ecommerce: {
          currencyCode: "RUB",
          add: {
            products: [
              {
                name: item.product.name,
                id: item.product.id,
                price: item.price,
                category: "",
                quantity: item.quantity,
                brand: item.product.brand?.name || "",
                dimension1: createBadgesString(item.product.badges),
                dimension2: item.product.isBundle,
                position:
                  cartItems.findIndex(
                    i =>
                      i.product.id === item.product.id &&
                      i.product.priceTypeID === item.product.priceTypeID
                  ) + 1,
              },
            ],
            actionField: {
              list: "Корзина",
            },
          },
        },
        event: "gtm-ee-event",
        "gtm-ee-event-category": "Enhanced Ecommerce",
        "gtm-ee-event-action": "Adding a Product to a Shopping Cart",
        "gtm-ee-event-non-interaction": false,
      });
    },
    onRemove: (cartItems: Array<Entity.Cart.Item>, item: Entity.Cart.Item) => {
      pushEvent({
        ecommerce: {
          currencyCode: "RUB",
          remove: {
            products: [
              {
                name: item.product.name,
                id: item.product.id,
                price: item.price,
                category: "",
                quantity: item.quantity,
                brand: item.product.brand?.name || "",
                position:
                  cartItems.findIndex(
                    i =>
                      i.product.id === item.product.id &&
                      i.product.priceTypeID === item.product.priceTypeID
                  ) + 1,
              },
            ],
            actionField: {
              list: "Корзина",
            },
          },
        },
        event: "gtm-ee-event",
        "gtm-ee-event-category": "Enhanced Ecommerce",
        "gtm-ee-event-action": "Removing a Product from a Shopping Cart",
        "gtm-ee-event-non-interaction": false,
      });
    },
    fullNameModal: {
      open: () => {
        pushEvent({ event: "Показ блока ФИО" });
      },
      payButtonClicked: () => {
        pushEvent({ event: "Нажатие на кнопку оплатить из блока ФИО" });
      },
      error: () => {
        pushEvent({ event: "Ошибка при заполнении блока ФИО" });
      },
      rulesHover: () => {
        pushEvent({ event: "Наведение курсора на Правила получения" });
      },
    },
    onFirstMapMarkRendered: () => {
      pushEvent({ event: "Показ первого пина аптеки на карте" });
    },
    onMapPharmacyInfoRendered: () => {
      pushEvent({ event: "Загрузка инфо об аптеке" });
    },
  },
  orderSuccess: {
    admitad: (order: Entity.Order) => {
      pushEvent({
        event: "admitad_purchase_ready",
        transactionId: order.id,
        transactionChannel: order.counters.admitad.transactionChannel,
        transactionAction: order.counters.admitad.transactionAction,
        transactionProducts: order.items.map(
          ({ quantity, price, product }) => ({
            sku: product.bitrixID,
            tariff: order.counters.admitad.tariffCode,
            priceCurrency: "RUB",
            price,
            quantity,
          })
        ),
      });
    },
    onCancelOrder: (orderId: Entity.ID) => {
      pushEvent({
        event: "Отмена заказа",
        transactionID: orderId,
        block: "checkout_success",
      });
    },
  },
  emias: {
    clickButtonAddPrescription: () => {
      pushEvent({
        eventCategory: "Enhanced Ecommerce",
        event: "Adding a Product to a Shopping Cart",
        block: "EMIAS",
      });
    },
    onErrorAddingPrescription: (message: string) => {
      pushEvent({
        event: "emias_error",
        error_type: message,
        block: "EMIAS",
      });
    },
  },
};

// todo: сделать yandex metric helper API
export const yandexMetrica = {};
